/**
 * Caption font catalog by writing system (PLAT-11408, epic PLAT-11407).
 *
 * Captions render through Creatomate, whose text elements take exactly ONE
 * `font_family` — there is no fallback stack — so a caption in a non-Latin
 * script silently renders as tofu unless its font actually carries that
 * script's glyphs. This module is the single source of truth for the curated
 * caption fonts per script, which known families cover which scripts, the
 * script-equivalent substitute per built-in caption preset font, and the
 * default caption font shared by both default karaoke configs.
 *
 * Every family listed here is a Google Font: the renderer resolves Google
 * families server-side by name, so nothing needs adding to `source.fonts[]`.
 * The editor preview loads its glyphs via the Google Fonts `@import`s in
 * `src/styles/globals.css` — a family added here MUST also be added there
 * (families and pinned weights are pinned by the drift-guard test in
 * `__tests__/captionFonts.test.ts`).
 *
 * Known limits, on purpose:
 * - Brand/custom fonts are absent from the coverage map, so they read as
 *   Latin-only and get substituted for non-Latin captions even when they do
 *   carry the glyphs — the deliberate anti-tofu bias until brand-font
 *   coverage capture lands (PLAT-11412, epic T8).
 * - The cyrillic/korean sets overlap `FONTS` in `general.ts` (Oswald,
 *   Montserrat, Noto Sans, Roboto Condensed, PT Sans Narrow, Nanum Gothic).
 *   A picker generalizing today's `[...FONTS, ...JAPANESE_FONTS]` concat to
 *   these sets (PLAT-11415, epic T5) must dedupe — this module cannot, since
 *   importing `FONTS` would break the zero-runtime-imports rule below.
 * - The display faces (Black Han Sans ≈ the KS X 1001 syllable set, ZCOOL
 *   QingKe HuangYou) cover common-usage subsets of their scripts, not every
 *   rare glyph; the Noto faces are complete. The render spike should verify
 *   them with rare-glyph strings, not just everyday sample text.
 *
 * Intentionally zero runtime imports (the `CaptionScript` import is type-only)
 * so `general.ts` can re-export from here without any risk of import cycles.
 */

import type { CaptionScript } from './captionLanguage';

type NonLatinCaptionScript = Exclude<CaptionScript, 'latin'>;

/**
 * The default caption font — font fields only, spread by both
 * `DEFAULT_KARAOKE_CONFIG` (services) and `DEFAULT_EDITOR_KARAOKE_CONFIG`
 * (editor) so the default can't drift between them. Anton covers Latin
 * (+ Vietnamese) only; non-Latin caption languages need a substitute.
 */
export const DEFAULT_CAPTION_FONT = {
  font_family: 'Anton',
  font_weight: '400',
  font_style: 'normal',
} as const;

/**
 * Curated caption fonts per script, generalizing the Japanese-only
 * `JAPANESE_FONTS` list from PLAT-10420 (`general.ts` re-exports the
 * `japanese` entry under that name for its existing picker consumers).
 * `latin` is empty on purpose — the main `FONTS` list already serves Latin.
 */
export const CAPTION_FONTS_BY_SCRIPT: Record<CaptionScript, readonly string[]> =
  {
    latin: [],
    cyrillic: [
      'Oswald',
      'Montserrat',
      'Noto Sans',
      'Roboto Condensed',
      'PT Sans Narrow',
    ],
    japanese: [
      'Dela Gothic One',
      'Noto Sans JP',
      'Shippori Mincho',
      'Zen Kaku Gothic New',
      'Zen Maru Gothic',
    ],
    'chinese-simplified': ['Noto Sans SC', 'ZCOOL QingKe HuangYou'],
    'chinese-traditional': ['Noto Sans TC'],
    korean: ['Noto Sans KR', 'Black Han Sans', 'Nanum Gothic'],
  };

/** The lookup key for a family name — trimmed, case-insensitive. */
export const normalizeCaptionFontFamilyKey = (
  family: string | null | undefined,
): string => (family ?? '').trim().toLowerCase();

const normalizeFamilyKey = normalizeCaptionFontFamilyKey;

const hasOwn = (record: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(record, key);

/**
 * Which non-Latin scripts each known family can render. CURATED, NOT
 * EXHAUSTIVE: a family absent from this map is assumed Latin-only, which is
 * the conservative direction — a false "doesn't cover" at worst swaps in a
 * substitute that definitely renders, while a false "covers" would ship tofu.
 * Coverage claims are per the fonts' Google Fonts subset listings.
 */
const FONT_SCRIPT_COVERAGE: Record<string, readonly CaptionScript[]> = {
  // Cyrillic-capable families already in `FONTS` / the globals.css import.
  oswald: ['cyrillic'],
  montserrat: ['cyrillic'],
  'noto sans': ['cyrillic'],
  roboto: ['cyrillic'],
  'roboto condensed': ['cyrillic'],
  'roboto mono': ['cyrillic'],
  'roboto slab': ['cyrillic'],
  'open sans': ['cyrillic'],
  'pt sans': ['cyrillic'],
  'pt sans narrow': ['cyrillic'],
  'pt serif': ['cyrillic'],
  inter: ['cyrillic'],
  'fira sans': ['cyrillic'],
  'ibm plex sans': ['cyrillic'],
  rubik: ['cyrillic'],
  ubuntu: ['cyrillic'],
  caveat: ['cyrillic'],
  lobster: ['cyrillic'],
  arimo: ['cyrillic'],
  // Non-Latin curated sets.
  'dela gothic one': ['japanese', 'cyrillic'],
  'noto sans jp': ['japanese'],
  'shippori mincho': ['japanese'],
  'zen kaku gothic new': ['japanese'],
  'zen maru gothic': ['japanese'],
  'noto sans sc': ['chinese-simplified'],
  'zcool qingke huangyou': ['chinese-simplified'],
  'noto sans tc': ['chinese-traditional'],
  'noto sans kr': ['korean'],
  'black han sans': ['korean'],
  'nanum gothic': ['korean'],
};

const coversByKey = (familyKey: string, script: CaptionScript): boolean => {
  if (script === 'latin') return true;
  if (!hasOwn(FONT_SCRIPT_COVERAGE, familyKey)) return false;
  return FONT_SCRIPT_COVERAGE[familyKey].includes(script);
};

/**
 * Whether `family` can render `script`. `latin` is always true — every family
 * we ship renders basic Latin (CJK families carry full Latin glyph sets).
 * Lookup is case-insensitive and trimmed so a persisted casing variant can't
 * cause a needless substitution. Unknown families → false for non-Latin
 * scripts (assumed Latin-only; see `FONT_SCRIPT_COVERAGE`). Nullish input is
 * tolerated (legacy persisted configs are loosely typed) and reads as
 * unknown.
 */
export const fontCoversScript = (
  family: string | null | undefined,
  script: CaptionScript,
): boolean => coversByKey(normalizeFamilyKey(family), script);

/**
 * Whether this module has ANY information about `family` — i.e. it is one of
 * the curated/preset families rather than a brand/custom upload. Read-time
 * (hydrate) correction only substitutes known families: an unknown family may
 * well cover the script even though the coverage map can't see it (brand-font
 * coverage arrives with PLAT-11412), and silently re-correcting a deliberate
 * pick on every load would be an unwinnable fight against the user (DE1).
 */
export const isKnownCaptionFontFamily = (
  family: string | null | undefined,
): boolean => {
  const key = normalizeFamilyKey(family);
  return hasOwn(FONT_SCRIPT_COVERAGE, key) || hasOwn(PRESET_SUBSTITUTES, key);
};

export type CaptionFontSubstitute = {
  font_family: string;
  /** Omitted = keep the config's current weight (variable-weight substitute). */
  font_weight?: string;
  /**
   * Pinned to `'normal'` on families that ship no italic face; omitted when
   * the substitute has real italics so the config's current style can stay.
   */
  font_style?: string;
};

/**
 * Script-equivalent substitutes for the built-in caption preset fonts, chosen
 * to preserve each preset's personality (Anton is a tall condensed display
 * face, so its Japanese stand-in is Dela Gothic One, not a generic sans).
 * Weights are pinned to faces the globals.css imports actually load; the
 * display faces (Dela Gothic One, ZCOOL QingKe HuangYou, Black Han Sans) only
 * ship weight 400, and none of the CJK faces nor Oswald ship an italic, so
 * those entries pin `font_style: 'normal'`. Edge × cyrillic could
 * alternatively be Dela Gothic One (it has a Cyrillic subset) — flagged for a
 * design pass; Montserrat 800 is the safe default.
 */
const PRESET_SUBSTITUTES: Record<
  string,
  Partial<Record<NonLatinCaptionScript, CaptionFontSubstitute>>
> = {
  // Impact — the default preset (key derived from the shared constant so a
  // default-font change can't silently orphan this row).
  [normalizeFamilyKey(DEFAULT_CAPTION_FONT.font_family)]: {
    cyrillic: {
      font_family: 'Oswald',
      font_weight: '600',
      font_style: 'normal',
    },
    japanese: {
      font_family: 'Dela Gothic One',
      font_weight: '400',
      font_style: 'normal',
    },
    'chinese-simplified': {
      font_family: 'ZCOOL QingKe HuangYou',
      font_weight: '400',
      font_style: 'normal',
    },
    'chinese-traditional': {
      font_family: 'Noto Sans TC',
      font_weight: '900',
      font_style: 'normal',
    },
    korean: {
      font_family: 'Black Han Sans',
      font_weight: '400',
      font_style: 'normal',
    },
  },
  // Classic.
  poppins: {
    cyrillic: { font_family: 'Montserrat', font_weight: '700' },
    japanese: {
      font_family: 'Noto Sans JP',
      font_weight: '700',
      font_style: 'normal',
    },
    'chinese-simplified': {
      font_family: 'Noto Sans SC',
      font_weight: '700',
      font_style: 'normal',
    },
    'chinese-traditional': {
      font_family: 'Noto Sans TC',
      font_weight: '700',
      font_style: 'normal',
    },
    korean: {
      font_family: 'Noto Sans KR',
      font_weight: '700',
      font_style: 'normal',
    },
  },
  // Typewriter.
  'dm mono': {
    cyrillic: { font_family: 'Roboto Mono', font_weight: '500' },
    japanese: {
      font_family: 'Noto Sans JP',
      font_weight: '500',
      font_style: 'normal',
    },
    'chinese-simplified': {
      font_family: 'Noto Sans SC',
      font_weight: '500',
      font_style: 'normal',
    },
    'chinese-traditional': {
      font_family: 'Noto Sans TC',
      font_weight: '500',
      font_style: 'normal',
    },
    korean: {
      font_family: 'Noto Sans KR',
      font_weight: '500',
      font_style: 'normal',
    },
  },
  // Edge.
  'special gothic expanded one': {
    cyrillic: { font_family: 'Montserrat', font_weight: '800' },
    japanese: {
      font_family: 'Dela Gothic One',
      font_weight: '400',
      font_style: 'normal',
    },
    'chinese-simplified': {
      font_family: 'Noto Sans SC',
      font_weight: '900',
      font_style: 'normal',
    },
    'chinese-traditional': {
      font_family: 'Noto Sans TC',
      font_weight: '900',
      font_style: 'normal',
    },
    korean: {
      font_family: 'Black Han Sans',
      font_weight: '400',
      font_style: 'normal',
    },
  },
  // Black & White. (Inter itself covers Cyrillic, so no cyrillic entry.)
  inter: {
    japanese: {
      font_family: 'Noto Sans JP',
      font_weight: '600',
      font_style: 'normal',
    },
    'chinese-simplified': {
      font_family: 'Noto Sans SC',
      font_weight: '600',
      font_style: 'normal',
    },
    'chinese-traditional': {
      font_family: 'Noto Sans TC',
      font_weight: '600',
      font_style: 'normal',
    },
    korean: {
      font_family: 'Noto Sans KR',
      font_weight: '600',
      font_style: 'normal',
    },
  },
};

/**
 * Fallback for families with no preset mapping: the script's Noto face,
 * keeping the config's current weight (all four are variable 100..900).
 * Deliberately a full `Record` over the non-Latin scripts — adding a script
 * to `CaptionScript` must be a compile error here, never a silent `null`
 * (= "no substitution needed" = tofu).
 */
const FALLBACK_SUBSTITUTES: Record<
  NonLatinCaptionScript,
  CaptionFontSubstitute
> = {
  cyrillic: { font_family: 'Noto Sans' },
  japanese: { font_family: 'Noto Sans JP', font_style: 'normal' },
  'chinese-simplified': { font_family: 'Noto Sans SC', font_style: 'normal' },
  'chinese-traditional': {
    font_family: 'Noto Sans TC',
    font_style: 'normal',
  },
  korean: { font_family: 'Noto Sans KR', font_style: 'normal' },
};

/**
 * The font to substitute when a caption styled with `fontFamily` switches to a
 * language written in `script` — or `null` when no substitution is needed
 * (the script is Latin, or the current font already covers it; a covering
 * font, including a manual user pick, must never be overridden — epic DE1).
 * Always returns a fresh object; callers may merge/mutate the result freely.
 */
export const getCaptionFontSubstitute = (
  fontFamily: string | null | undefined,
  script: CaptionScript,
): CaptionFontSubstitute | null => {
  if (script === 'latin') return null;
  const familyKey = normalizeFamilyKey(fontFamily);
  if (coversByKey(familyKey, script)) return null;
  const presetRow = hasOwn(PRESET_SUBSTITUTES, familyKey)
    ? PRESET_SUBSTITUTES[familyKey]
    : undefined;
  return { ...(presetRow?.[script] ?? FALLBACK_SUBSTITUTES[script]) };
};

/**
 * Every substitute this module can hand out — preset rows plus fallbacks.
 * Used by the drift-guard test to pin each pinned weight to the globals.css
 * imports, and by future preloading work.
 */
export const ALL_CAPTION_FONT_SUBSTITUTES: readonly CaptionFontSubstitute[] = [
  ...Object.values(PRESET_SUBSTITUTES).flatMap(byScript =>
    Object.values(byScript).filter(
      (substitute): substitute is CaptionFontSubstitute => !!substitute,
    ),
  ),
  ...Object.values(FALLBACK_SUBSTITUTES),
];

/**
 * Every family this module can put on a caption element — catalog entries
 * plus every substitute family. Each must be loaded by the globals.css
 * imports (drift-guard test) and resolvable by name on the render service.
 */
export const ALL_CAPTION_FONT_FAMILIES: readonly string[] = [
  ...new Set([
    ...Object.values(CAPTION_FONTS_BY_SCRIPT).flat(),
    ...ALL_CAPTION_FONT_SUBSTITUTES.map(substitute => substitute.font_family),
  ]),
];
